import { useMedicareUrl } from 'hooks/useMedicareUrl'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Divider } from '../../../../components/Divider/Divider'
import { MyInternalLink, MyLink } from 'components/MyLink'
import { CLAIMS_URLS_ENGLISH, CLAIMS_URLS_SPANISH, ClaimsUrls } from 'containers/Claims/urls'

interface ResourceList {
  text: string
  link: string
}

interface RelatedResourceSectionProps {
  heading: string
  list: ResourceList[]
}

const RelatedResourceSection: FC<RelatedResourceSectionProps> = ({ heading, list }) => {
  return (
    <div style={{
      flex: 1
    }}
    >
      <Divider className='my-u-my--12' />
      <h3 className='my-u-m--0 my-u-mb--10 ds-text-heading--xl'>{heading}</h3>
      <ul
        className='ds-u-margin--0 ds-u-padding--0' style={{
          listStyle: 'none'
        }}
      >
        {list.map(
          ({ text, link }) => {
            const isExternal = /^https?:\/\//.test(link)
            return (
              <li key={text} className='my-u-mb--10'>
                {isExternal
                  ? (
                      <MyLink href={link}>{text}</MyLink>
                    )
                  : (
                      <MyInternalLink to={link}>{text}</MyInternalLink>
                    )
                }
              </li>
            )
          }
        )}
      </ul>
    </div>
  )
}

function useRelatedResourcesLink (key: keyof ClaimsUrls) {
  return useMedicareUrl(
    CLAIMS_URLS_ENGLISH[key],
    CLAIMS_URLS_SPANISH[key]
  )
}

export const RelatedResources: FC = () => {
  const { t } = useTranslation('eMSNPage')
  const howToReadUrl = useRelatedResourcesLink('formsHelpMsn')
  const goPaperlessPath = '/account-settings'
  const howToSpotMedicareFraudUrl = useRelatedResourcesLink('howToSpotFraud')
  const medicareCostUrl = useMedicareUrl(
    'https://www.medicare.gov/your-medicare-costs',
    'https://es.medicare.gov/your-medicare-costs'
  )
  const helpPayingCostsUrl = useRelatedResourcesLink('getHelpPayingCosts')
  const fileAppealUrl = useRelatedResourcesLink('howToFileAnAppeal')
  const thingsToKnowUrl = useRelatedResourcesLink('fiveThingsToKnowWhenFilingAnAppeal')
  const helpFilingUrl = useRelatedResourcesLink('canSomeoneFileAnAppealForMe')

  return (
    <div className='my-u-mt--30 my-u-mb--40'>
      <h2 className='ds-text-heading--xl my-u-m--0'>{t('related-resources.heading')}</h2>
      <div
        className='ds-u-display--flex ds-u-flex-direction--column ds-u-md-flex-direction--row' style={{
          gap: '20px'
        }}
      >
        <RelatedResourceSection
          heading={t('related-resources.emsns-heading')} list={[
            {
              text: t('related-resources.how-to-read'),
              link: howToReadUrl
            },
            {
              text: t('related-resources.go-paperless'),
              link: goPaperlessPath
            },
            {
              text: t('related-resources.learn-how'),
              link: howToSpotMedicareFraudUrl
            }
          ]}
        />
        <RelatedResourceSection
          heading={t('related-resources.learn-more-heading')} list={[
            {
              text: t('related-resources.medicare-costs'),
              link: medicareCostUrl
            },
            {
              text: t('related-resources.help-paying-costs'),
              link: helpPayingCostsUrl
            }
          ]}
        />
        <RelatedResourceSection
          heading={t('related-resources.appeals-heading')} list={[
            {
              text: t('related-resources.file-appeal'),
              link: fileAppealUrl
            },
            {
              text: t('related-resources.things-to-know'),
              link: thingsToKnowUrl
            },
            {
              text: t('related-resources.help-filing'),
              link: helpFilingUrl
            }
          ]}
        />
      </div>
    </div>
  )
}
